import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import FieldMapping from './FieldMapping';
import CustomHeaders from './CustomHeaders';
import ConditionalLogic from './ConditionalLogic';
import WebhookLogs from './WebhookLogs';

const DEFAULT_FORM = {
	title:         '',
	status:        'publish',
	url:           '',
	method:        'POST',
	cf7_form_id:   '',
	cond_enabled:  false,
	cond_field:    '',
	cond_operator: 'equals',
	cond_value:    '',
	headers:       [],
	mapping:       [],
};

export default function WebhookEditor( { webhookId, onSaved, onCancel, showNotice } ) {
	const [ form, setForm ]         = useState( DEFAULT_FORM );
	const [ logs, setLogs ]         = useState( [] );
	const [ cf7Forms, setCf7Forms ] = useState( [] );
	const [ cf7Fields, setCf7Fields ] = useState( [] );
	const [ loading, setLoading ]   = useState( !! webhookId );
	const [ saving, setSaving ]     = useState( false );
	const [ activeTab, setActiveTab ] = useState( 'details' );

	// Load CF7 forms
	useEffect( () => {
		apiFetch( { path: '/rtwc/v1/cf7-forms' } )
			.then( setCf7Forms )
			.catch( () => {} );
	}, [] );

	// Load existing webhook
	useEffect( () => {
		if ( ! webhookId ) {
			setForm( DEFAULT_FORM );
			setLogs( [] );
			setLoading( false );
			return;
		}
		setLoading( true );
		apiFetch( { path: `/rtwc/v1/webhooks/${ webhookId }` } )
			.then( ( data ) => {
				setForm( {
					title:         data.title || '',
					status:        data.status || 'publish',
					url:           data.url || '',
					method:        data.method || 'POST',
					cf7_form_id:   data.cf7_form_id || '',
					cond_enabled:  data.cond_enabled || false,
					cond_field:    data.cond_field || '',
					cond_operator: data.cond_operator || 'equals',
					cond_value:    data.cond_value || '',
					headers:       data.headers || [],
					mapping:       data.mapping || [],
				} );
				setLogs( data.logs || [] );
			} )
			.catch( () => showNotice( 'error', 'Failed to load webhook.' ) )
			.finally( () => setLoading( false ) );
	}, [ webhookId ] );

	// Fetch CF7 fields when form changes
	useEffect( () => {
		if ( ! form.cf7_form_id ) {
			setCf7Fields( [] );
			return;
		}
		apiFetch( { path: `/rtwc/v1/cf7-fields/${ form.cf7_form_id }` } )
			.then( setCf7Fields )
			.catch( () => setCf7Fields( [] ) );
	}, [ form.cf7_form_id ] );

	const set = ( key ) => ( val ) => setForm( ( prev ) => ( { ...prev, [ key ]: val } ) );

	const handleSave = async () => {
		if ( ! form.url ) {
			showNotice( 'error', 'Webhook URL is required.' );
			return;
		}
		setSaving( true );
		try {
			if ( webhookId ) {
				await apiFetch( {
					path:   `/rtwc/v1/webhooks/${ webhookId }`,
					method: 'PUT',
					data:   form,
				} );
			} else {
				await apiFetch( {
					path:   '/rtwc/v1/webhooks',
					method: 'POST',
					data:   form,
				} );
			}
			onSaved();
		} catch ( e ) {
			showNotice( 'error', e?.message || 'Save failed.' );
		} finally {
			setSaving( false );
		}
	};

	const handleClearLogs = async () => {
		if ( ! webhookId ) return;
		try {
			await apiFetch( { path: `/rtwc/v1/webhooks/${ webhookId }/clear-logs`, method: 'DELETE' } );
			setLogs( [] );
			showNotice( 'success', 'Logs cleared.' );
		} catch {
			showNotice( 'error', 'Failed to clear logs.' );
		}
	};

	if ( loading ) {
		return (
			<div className="rtwc-loading">
				<span className="spinner is-active" />
				<span>Loading…</span>
			</div>
		);
	}

	const tabs = [
		{ id: 'details',   label: 'Details' },
		{ id: 'mapping',   label: 'Field Mapping' },
		{ id: 'headers',   label: 'Custom Headers' },
		{ id: 'condition', label: 'Conditional Logic' },
		...( webhookId ? [ { id: 'logs', label: `Logs (${ logs.length })` } ] : [] ),
	];

	return (
		<div className="rtwc-editor">
			{/* Title + Status row */}
			<div className="rtwc-editor-titlebar">
				<div className="rtwc-title-field">
					<label className="rtwc-label">Webhook Name</label>
					<input
						type="text"
						className="regular-text rtwc-title-input"
						value={ form.title }
						placeholder="e.g. Send to CRM"
						onChange={ ( e ) => set( 'title' )( e.target.value ) }
					/>
				</div>
				<div className="rtwc-status-toggle">
					<label className="rtwc-label">Status</label>
					<div className="rtwc-toggle-group">
						{ [ 'publish', 'draft' ].map( ( s ) => (
							<button
								key={ s }
								className={ `rtwc-toggle ${ form.status === s ? 'active' : '' }` }
								onClick={ () => set( 'status' )( s ) }
							>
								{ s === 'publish' ? '● Active' : '○ Draft' }
							</button>
						) ) }
					</div>
				</div>
			</div>

			{/* Tab navigation */}
			<div className="rtwc-tabs">
				{ tabs.map( ( t ) => (
					<button
						key={ t.id }
						className={ `rtwc-tab ${ activeTab === t.id ? 'rtwc-tab--active' : '' }` }
						onClick={ () => setActiveTab( t.id ) }
					>
						{ t.label }
					</button>
				) ) }
			</div>

			<div className="rtwc-tab-content">
				{ activeTab === 'details' && (
					<DetailsTab form={ form } set={ set } cf7Forms={ cf7Forms } />
				) }
				{ activeTab === 'mapping' && (
					<FieldMapping
						mapping={ form.mapping }
						onChange={ set( 'mapping' ) }
						cf7Fields={ cf7Fields }
					/>
				) }
				{ activeTab === 'headers' && (
					<CustomHeaders
						headers={ form.headers }
						onChange={ set( 'headers' ) }
					/>
				) }
				{ activeTab === 'condition' && (
					<ConditionalLogic
						enabled={ form.cond_enabled }
						field={ form.cond_field }
						operator={ form.cond_operator }
						value={ form.cond_value }
						cf7Fields={ cf7Fields }
						onChange={ ( patch ) => setForm( ( prev ) => ( { ...prev, ...patch } ) ) }
					/>
				) }
				{ activeTab === 'logs' && webhookId && (
					<WebhookLogs logs={ logs } onClear={ handleClearLogs } />
				) }
			</div>

			{/* Action buttons */}
			<div className="rtwc-editor-footer">
				<button
					className="button button-primary"
					onClick={ handleSave }
					disabled={ saving }
				>
					{ saving ? 'Saving…' : ( webhookId ? 'Update Webhook' : 'Create Webhook' ) }
				</button>
				{ ' ' }
				<button className="button" onClick={ onCancel } disabled={ saving }>
					Cancel
				</button>
			</div>
		</div>
	);
}

function DetailsTab( { form, set, cf7Forms } ) {
	return (
		<table className="form-table rtwc-form-table">
			<tbody>
				<tr>
					<th><label>Webhook URL <span className="required">*</span></label></th>
					<td>
						<input
							type="url"
							className="large-text"
							value={ form.url }
							placeholder="https://example.com/webhook"
							onChange={ ( e ) => set( 'url' )( e.target.value ) }
						/>
						<p className="description">The endpoint URL that will receive the POST/GET/PUT request.</p>
					</td>
				</tr>
				<tr>
					<th><label>HTTP Method</label></th>
					<td>
						<div className="rtwc-method-group">
							{ [ 'POST', 'GET', 'PUT' ].map( ( m ) => (
								<button
									key={ m }
									type="button"
									className={ `rtwc-method-btn rtwc-method-btn--${ m.toLowerCase() } ${ form.method === m ? 'active' : '' }` }
									onClick={ () => set( 'method' )( m ) }
								>
									{ m }
								</button>
							) ) }
						</div>
					</td>
				</tr>
				<tr>
					<th><label>Contact Form 7</label></th>
					<td>
						<select
							value={ form.cf7_form_id }
							onChange={ ( e ) => set( 'cf7_form_id' )( e.target.value ) }
						>
							<option value="">— Select a CF7 Form —</option>
							{ cf7Forms.map( ( f ) => (
								<option key={ f.id } value={ f.id }>{ f.title }</option>
							) ) }
						</select>
						<p className="description">Select which CF7 form triggers this webhook.</p>
					</td>
				</tr>
			</tbody>
		</table>
	);
}
